import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { setErrorsOnFormAndNotifyGeneralErrors as notifyErrors } from '../../helpers/generalHelpers'
import { typeUtils } from '../../types'
import { form } from 'api/services/form'

const getFormFn = async (formSlug, setLoading, cancelToken) => {
  setLoading(true)
  const [err, res] = await form.getOne(formSlug, { cancelToken })

  if (res) {
    return Promise.resolve(res.form)
  }

  notifyErrors(err)
  return Promise.reject(err)
}

const getProcessedForm = (form) => {
  if (!form) return undefined

  const { fields_list = [] } = form

  const separatedFields = {
    adminFields: [],
    hiddenFields: [],
    visibleFields: [],
    nonRenderableFields: [],
    variableFields: [],
    formulaFields: [],
    successPages: [],
  }

  for (const field of fields_list) {
    if (typeUtils.isAdminOnly(field)) {
      separatedFields.adminFields.push(field)
    } else if (typeUtils.isHidden(field)) {
      separatedFields.hiddenFields.push(field)
    } else if (typeUtils.isNonRenderable(field)) {
      separatedFields.nonRenderableFields.push(field)
    } else if (typeUtils.isFormula(field)) {
      separatedFields.formulaFields.push(field)
    } else if (typeUtils.isVariable(field)) {
      separatedFields.variableFields.push(field)
    } else if (typeUtils.isSuccessPage(field)) {
      separatedFields.successPages.push(field)
    } else {
      separatedFields.visibleFields.push(field)
    }
  }

  return separatedFields
}

const CancelToken = axios.CancelToken

const useFormalooForm = (formSlug) => {
  const [theForm, setTheForm] = useState(null)
  const [theErrors, setErrors] = useState(null)
  const [isLoading, setLoading] = useState(false)

  const memoizedGetForm = useCallback(getFormFn, [formSlug, setLoading])
  const getFieldsSeparatedByType = useCallback(getProcessedForm, [theForm])

  const setForm = useCallback(
    (form) => {
      setTheForm({
        ...form,
        ...getFieldsSeparatedByType(form),
      })
    },
    [getFieldsSeparatedByType]
  )

  useEffect(() => {
    const source = CancelToken.source()

    memoizedGetForm(formSlug, setLoading, source.token)
      .then((form) => setForm(form))
      .catch((error) => setErrors({ error }))
      .finally(() => setLoading(false))

    // cancel request onUnmount
    return () => {
      source.cancel()
    }
  }, [formSlug, memoizedGetForm, setForm])

  return {
    isLoading,
    form: theForm,
    errors: theErrors,
    setForm,
  }
}

export default useFormalooForm
